import { NavLink, useLocation } from 'react-router-dom';

import { buttonVariants } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { isDestinationRoute, PROFILE_ITEM } from '@/config/navigation';
import { cn } from '@/utils/helpers/cn';

/**
 * O botão do perfil, no topo das telas-destino.
 *
 * Ocupa o lugar onde ficava o hambúrguer: menu e perfil trocaram de posição
 * (planilha, 2026-10) — o menu virou a última célula da barra inferior
 * (`AppMenuTab`) e o perfil subiu para cá. Nas telas com cabeçalho ele entra no
 * slot `actions` do `PageHeader`; no mapa, flutua ao lado da busca, com a
 * moldura que a página passa por `className`.
 *
 * ## Link, e não botão
 *
 * Ele leva a uma tela, então é `<a>`: abrir em outra aba, o "voltar" do
 * navegador e o anúncio do leitor de tela ("link, Perfil") funcionam sem
 * esforço. `NavLink` dá `aria-current="page"` em `/profile` — o equivalente da
 * aba acesa que o perfil tinha na barra —, e a cor primária repete o estado
 * para quem enxerga.
 *
 * O histórico segue a régua das abas (`isDestinationRoute`): saindo de uma
 * tela-destino, substitui a entrada em vez de empilhar; saindo de um detalhe,
 * empilha, porque ali o "voltar" precisa devolver o que a pessoa via.
 *
 * Convidado também chega aqui: o Perfil sem sessão mostra os caminhos de
 * entrada (`GuestAccessSection`), então não há gate no botão.
 */
export function ProfileButton({ className }: { className?: string }) {
  const { pathname } = useLocation();

  return (
    <NavLink
      to={PROFILE_ITEM.to}
      replace={isDestinationRoute(pathname)}
      // Só-ícone: sem `aria-label` o leitor de tela anuncia "link" e nada mais
      // (docs/03).
      aria-label={PROFILE_ITEM.label}
      className={({ isActive }) =>
        cn(
          buttonVariants({ variant: 'ghost', size: 'icon' }),
          isActive && 'text-primary',
          className,
        )
      }
    >
      <Icon as={PROFILE_ITEM.icon} size="lg" />
    </NavLink>
  );
}
